import React, { useState, useEffect } from "react";
import { Card, Row, Col, Divider, Typography, Calendar, Badge, Spin, Switch } from "antd";
import moment from "moment";
import axios from "axios";
import GoBr from "../../../Components/GoBr";

export const StudentAvailability = ({ studentId }) => {
    const [student, setStudent] = useState(null);
    const [pickupCalendar, setPickupCalendar] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFullCalendar, setShowFullCalendar] = useState(false);
    console.log("StudentAvailability received studentId:", studentId);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Get stored values from localStorage
                const account = JSON.parse(localStorage.getItem("account") || "{}");
                const baseUrl = localStorage.getItem("api");

                if (!account || !account.access_token) {
                    setError("Missing authentication token. Please log in again.");
                    setLoading(false);
                    return;
                }

                if (!baseUrl) {
                    setError("Missing base URL. Please configure your settings.");
                    setLoading(false);
                    return;
                }

                const response = await axios.get(
                    `${baseUrl}/pickup/student/new/updated`,
                    {
                        params: { student_id: studentId },
                        headers: {
                            Authorization: `Bearer ${account.access_token}`
                        }
                    }
                );

                if (response.data && response.data.student) {
                    setStudent(response.data.student);
                    setPickupCalendar(response.data.pickup_calendar || []);
                } else {
                    setError("Invalid response format from server");
                }
            } catch (err) {
                setError(err.message || "An error occurred while fetching data");
            } finally {
                setLoading(false);
            }
        };

        if (studentId) {
            fetchData();
        } else {
            setError("No student ID provided");
            setLoading(false);
        }
    }, [studentId]);

    const getAvailabilityData = (value) => {
        if (!pickupCalendar || !pickupCalendar.length) return [];

        const dateStr = value.format("YYYY-MM-DD");
        const entries = pickupCalendar.filter(entry => {
            const startDate = moment(entry.start_date);
            const endDate = moment(entry.end_date);
            const currentDate = moment(dateStr);
            return currentDate.isBetween(startDate, endDate, null, '[]');
        });

        if (entries.length === 0) return [];

        const morning = entries.some(e => e.morning);
        const evening = entries.some(e => e.evening);

        const list = [];
        if (morning && evening) {
            list.push({ type: 'success', content: 'Full day' });
        } else if (morning) {
            list.push({ type: 'warning', content: 'Morning' });
        } else if (evening) {
            list.push({ type: 'error', content: 'Evening' });
        }

        return list;
    };

    const dateCellRender = (value) => {
        const listData = getAvailabilityData(value);
        return (
            <div className="events" style={{ height: '100%' }}>
                {listData.map((item, index) => (
                    <div key={index} style={{
                        position: 'absolute',
                        width: '100%',
                        height: item.content === 'Morning' ? '50%' :
                            item.content === 'Evening' ? '50%' : '100%',
                        bottom: item.content === 'Morning' ? 0 :
                            item.content === 'Evening' ? '50%' : 0,
                        backgroundColor: item.type === 'success' ? '#f6ffed' :
                            item.type === 'warning' ? '#fffbe6' : '#fff2f0',
                        border: `1px solid ${
                            item.type === 'success' ? '#b7eb8f' :
                                item.type === 'warning' ? '#ffe58f' : '#ffccc7'
                        }`
                    }}>
                        <Badge
                            key={item.content}
                            status={item.type}
                            text={item.content}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: 10,
                                whiteSpace: 'nowrap',
                                display: 'block' // Changed from conditional to always display
                            }}
                        />
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return <Typography.Text type="danger">{error}</Typography.Text>;
    }

    if (!student) {
        return <Typography.Text>No student data found</Typography.Text>;
    }

    return (
        <div style={{ padding: 24 }}>
            <Row gutter={16}>
                {/* Student Info Column - Reduced width */}
                <Col span={8}>
                    <Card bordered={false} className="shadow" style={{ borderRadius: 8 }}>
                        <Typography.Title level={3} type={"secondary"} style={{ marginBottom: 16 }}>Student Information</Typography.Title>
                        <Typography.Paragraph>
                            <strong>Name:</strong> {student.student_name}
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            <strong>ADM:</strong> {student.adm}
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            <strong>Class:</strong> {student.class} {student.stream}
                        </Typography.Paragraph>
                        <GoBr />
                        <Typography.Paragraph>
                            <strong>Route:</strong> {student.route_name}
                        </Typography.Paragraph>
                        <Typography.Paragraph>
                            <strong>Station:</strong> {student.station_name}
                        </Typography.Paragraph>
                        <GoBr />
                        <Typography.Paragraph>
                            <strong>Subscription:</strong> {student.subscription}
                        </Typography.Paragraph>
                    </Card>
                </Col>

                {/* Calendar Column - Increased width */}
                <Col span={16}>
                    <Card bordered={false} className="shadow" style={{ borderRadius: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <Typography.Title type={"secondary"} level={3}>Pickup Availability</Typography.Title>
                            <div>
                                <Switch
                                    checkedChildren="Full View"
                                    unCheckedChildren="Compact"
                                    checked={showFullCalendar}
                                    onChange={() => setShowFullCalendar(!showFullCalendar)}
                                />
                            </div>
                        </div>

                        <Calendar
                            dateCellRender={dateCellRender}
                            style={{
                                minHeight: showFullCalendar ? 500 : 300,
                                border: '1px solid #f0f0f0',
                                borderRadius: 8
                            }}
                        />

                        <Divider dashed />

                        <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
                            <Badge status="success" text="Full day available" />
                            <Badge status="warning" text="Morning only" />
                            <Badge status="error" text="Evening only" />
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};